import { useId, useRef, useState } from "react";
import {
  useDismiss,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from "@floating-ui/react";
import { Dropdown } from "../dropdown/Dropdown";
import { ListNavigation } from "../list-navigation/ListNavigation";
import { Props } from "./DropdownList.Parts";
import { useFloating } from "./use-floating/UseFloating";

/**
 * Use this for standard option menus (sort pickers, "more actions" menus, filter
 * selects, etc.). It handles all the floating-ui setup internally — `useFloating`,
 * `useListNavigation`, `useTypeahead`, `useDismiss`, and `useRole("listbox")` — so
 * you only need to pass `anchor`, `open`, and `onOpenChange`.
 *
 * Nested `ListItem` components self-register via `ItemContext`, so there's no need
 * to wire up `elementsRef` or `getItemProps` manually.
 *
 * If the content isn't a list of options, or you need custom navigation behaviour,
 * use `Dropdown` directly and set up `useInteractions` yourself.
 */
export const DropdownList = ({
  id: idProp,
  anchor,
  open,
  onOpenChange,
  offset,
  matchAnchorWidth,
  minWidth,
  minHeight,
  maxHeight,
  maxWidth,
  placement,
  floating,
  children,
}: Props) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const generatedId = useId();
  const id = idProp || generatedId;

  const { refs, floatingStyles, context } = useFloating({
    anchor,
    open,
    onOpenChange,
    offset,
    matchAnchorWidth,
    minWidth,
    minHeight,
    placement,
  });

  // Populated by each ListItem via useListItem — used by useListNavigation and
  // useTypeahead to manage focus and keyboard-driven item matching.
  const elementsRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);

  const { getFloatingProps, getItemProps } = useInteractions([
    useListNavigation(context, {
      listRef: elementsRef,
      activeIndex,
      onNavigate: setActiveIndex,
      focusItemOnOpen: true,
    }),
    useTypeahead(context, {
      listRef: labelsRef,
      activeIndex,
      onMatch: setActiveIndex,
    }),
    useDismiss(context),
    useRole(context, { role: "listbox" }),
  ]);

  if (!open) return null;

  return (
    <Dropdown
      id={id}
      context={context}
      refs={refs}
      floatingStyles={floatingStyles}
      getFloatingProps={getFloatingProps}
      maxHeight={maxHeight}
      maxWidth={maxWidth}
      minHeight={minHeight}
      minWidth={minWidth}
      initialFocus={undefined}
      floating={floating}
    >
      <ListNavigation
        elementsRef={elementsRef}
        labelsRef={labelsRef}
        activeIndex={activeIndex}
        getItemProps={getItemProps}
      >
        {children}
      </ListNavigation>
    </Dropdown>
  );
};
